"use client";

import { LogOut, Settings, Check, Plus, Search, ChevronRight } from "lucide-react";
import * as React from "react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from "@/components/ui/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { useQueryClient } from "@tanstack/react-query";
import posthog from "posthog-js";
import Link from "next/link";
import { useMyUser } from "@/hooks/auth/use-my-user";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { isCrossHost } from "@/lib/admin-hosts";
import { logout } from "@/services/auth.service";

export function NavUser({
  user,
}: {
  user: {
    name: string;
    email: string;
    avatar: string;
  };
}) {
  const { isMobile } = useSidebar();
  const { myUser } = useMyUser();

  const queryClient = useQueryClient();

  const handleLogout = async () => {
    queryClient.clear();
    posthog.reset();
    await logout();
  };

  const currentApp = myUser?.current_user?.data?.pylo_app?.data;
  const allUserApps = myUser?.my_users?.data?.map((u: any) => u?.pylo_app?.data) ?? [];

  const currentCompanyName = currentApp?.pylo_customer?.data?.name ?? "";

  // TODO: make this an infinite query and apply the search in the backend
  // Only show default apps (one per customer)
  const availableCompanies = allUserApps.filter(
    (app: any) => !!app?.pylo_customer?.data?.name && app?.is_default !== false,
  );

  const currentCompany = availableCompanies.find(
    (c: any) => c?.pylo_customer?.data?.name === currentCompanyName,
  );
  const otherCompanies = availableCompanies.filter(
    (c: any) => c?.pylo_customer?.data?.name !== currentCompanyName,
  );

  const [orgSearch, setOrgSearch] = React.useState("");
  const filteredOthers = otherCompanies.filter((c: any) =>
    c.pylo_customer?.data?.name.toLowerCase().includes(orgSearch.toLowerCase()),
  );

  // Unknown until mount: deciding on the server would mismatch hydration.
  const [location, setLocation] = React.useState<Location | null>(null);
  React.useEffect(() => setLocation(window.location), []);
  const opensNewTab = (c: any) =>
    location !== null && isCrossHost(c?.pylo_customer?.data?.subdomain ?? null, location);

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground focus-visible:ring-0 focus-visible:ring-offset-0">
              <Avatar className="h-6 w-6 min-w-6 shrink-0 rounded-md">
                <AvatarImage src={user.avatar} alt={user.name} />
                <AvatarFallback className="rounded-md bg-sidebar-accent text-xs font-medium text-muted-foreground">
                  {user.name
                    ? user.name.charAt(0).toUpperCase()
                    : user.email.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-semibold">{user.name || user.email}</span>
              </div>
              <ChevronRight className="ml-auto size-4" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-56 rounded-lg"
            side={isMobile ? "bottom" : "right"}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <Avatar className="h-8 w-6 rounded-lg">
                  <AvatarImage src={user.avatar} alt={user.name} />
                  <AvatarFallback className="rounded-sm">{user.name.charAt(0)}</AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">{user.name}</span>
                  <span className="truncate text-xs">{user.email}</span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>Current organization</DropdownMenuLabel>
            <DropdownMenuItem>
              <Avatar className="h-5 w-5 rounded-sm">
                <AvatarFallback className="rounded-sm">
                  {currentCompanyName.charAt(0)}
                </AvatarFallback>
              </Avatar>
              <div className="truncate" title={currentCompanyName}>
                {currentCompanyName}
              </div>
            </DropdownMenuItem>
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>Other organizations</DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="max-w-64" sideOffset={8}>
                <div className="p-1 py-2">
                  <div className="relative mb-2">
                    <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      value={orgSearch}
                      onChange={(e) => setOrgSearch(e.target.value)}
                      placeholder="Search organizations..."
                      className="pl-8"
                    />
                  </div>

                  {currentCompany && (
                    <>
                      <div className="px-1 pb-1 text-xs font-medium text-muted-foreground">
                        CURRENT ORGANIZATION
                      </div>
                      <DropdownMenuItem asChild>
                        <Button variant="ghost" className="w-full justify-start">
                          <div className="flex w-full items-center gap-2">
                            <Avatar className="h-5 w-5 rounded-sm">
                              <AvatarFallback className="rounded-sm">
                                {currentCompanyName.charAt(0)}
                              </AvatarFallback>
                            </Avatar>
                            <span className="truncate" title={currentCompanyName}>
                              {currentCompanyName}
                            </span>
                            <span className="ml-auto" />
                            <Check className="h-4 w-4 text-primary" />
                          </div>
                        </Button>
                      </DropdownMenuItem>
                    </>
                  )}

                  <div className="mt-2 px-1 pb-1 text-xs font-medium text-muted-foreground">
                    OTHER ORGANIZATIONS
                  </div>
                  {filteredOthers.map((c: any, idx: number) => (
                    <DropdownMenuItem key={idx} asChild>
                      <a
                        href={`/api/auth/switch?app_id=${c.id}`}
                        {...(opensNewTab(c) ? { target: "_blank", rel: "noopener" } : {})}
                        className="w-full"
                      >
                        <div className="flex max-w-full items-center gap-2">
                          <Avatar className="h-5 w-5 rounded-sm">
                            <AvatarFallback className="rounded-sm">
                              {c?.pylo_customer?.data?.name?.charAt(0)}
                            </AvatarFallback>
                          </Avatar>
                          <span className="truncate" title={c?.pylo_customer?.data?.name}>
                            {c?.pylo_customer?.data?.name}
                          </span>
                        </div>
                      </a>
                    </DropdownMenuItem>
                  ))}
                  {filteredOthers.length === 0 && (
                    <div className="px-1 text-xs text-muted-foreground">
                      No organizations found.
                    </div>
                  )}

                  <DropdownMenuSeparator />
                  <Button
                    variant="ghost"
                    className="w-full justify-start"
                    onMouseDown={(e) => e.preventDefault()}
                    disabled
                  >
                    <Plus className="mr-2 h-4 w-4" /> New organization
                  </Button>
                </div>
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            <DropdownMenuSeparator />

            <DropdownMenuLabel>Settings</DropdownMenuLabel>
            <DropdownMenuItem asChild>
              <Link href="/p/settings/organization">
                <Settings className="mr-2 h-4 w-4" />
                Settings
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout}>
              <LogOut className="mr-2 h-4 w-4" />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
